import { CircularProgress, Grid } from '@mui/material';
import { OptionType } from 'features/ui/base-components/pai-auto-complete';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiMenuItem from 'features/ui/base-components/pai-menu-item';
import PaiSelect from 'features/ui/base-components/pai-select';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BasicActionDialog } from '../../ui/base-dialogs';
import { SingleForm } from '../../../pages/motive-manage-surveys';
import type { CsvImportSummary, CsvPreviewRow } from './use-survey-management';
import CreateSurveyForm from './create-survey-form';

interface Props {
  open: boolean;
  loading: boolean;
  csvLoading: boolean;

  folders: OptionType[];
  selectedFolder: string;

  surveyTypes: OptionType[];
  selectedSurveyType: string;

  sendInvitationsFormState: SingleForm[];
  csvImportSummary: CsvImportSummary | null;
  csvPreviewRows: CsvPreviewRow[];
  notSendEmail: boolean;

  formChangeHandlerGenerator(idx: number, type: string): (e: React.ChangeEvent<HTMLInputElement>) => void;

  formChangeHandleDeleteGenerator(idx: number): () => void;

  addInvitations(): void;
  clearInvitations(): void;

  addInvitationsCSV(event: React.SyntheticEvent): void;
  addInvitationsCSVFromDrop(files: File[]): void;

  handleClose(): void;

  handleProceed(): void;

  handleSelectedFolderChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSelectedSurveyTypeChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleNotSendEmailChange(event: React.ChangeEvent<HTMLInputElement>): void;
}

interface State {
  isValid: boolean;
  isEmpty: boolean;
}

const CreateSurveyDialog: React.FC<Props> = ({
  folders,
  selectedFolder,
  surveyTypes,
  selectedSurveyType,
  handleSelectedFolderChange,
  handleSelectedSurveyTypeChange,
  handleClose,
  handleProceed,
  loading,
  csvLoading,
  sendInvitationsFormState,
  csvImportSummary,
  csvPreviewRows,
  notSendEmail,
  formChangeHandlerGenerator,
  formChangeHandleDeleteGenerator,
  addInvitations,
  clearInvitations,
  addInvitationsCSV,
  addInvitationsCSVFromDrop,
  handleNotSendEmailChange,
  open,
  ...remProps
}) => {
  const [state, setState] = React.useState<State>({
    isValid: false,
    isEmpty: true,
  });
  const { t } = useTranslation();

  useEffect(() => {
    const update = { ...state };

    update.isEmpty = sendInvitationsFormState.some((item) => {
      return item.email.text.length === 0;
    });
    update.isValid = !sendInvitationsFormState.some((item) => {
      return item.email.error;
    });

    setState(update);
  }, [sendInvitationsFormState]);

  return (
    <BasicActionDialog
      dialogTitle={t('sendInvitationsAt')}
      cancelButtonText={t('cancel')}
      proceedButtonText={loading ? <CircularProgress /> : t('send')}
      maxWidth={'md'}
      open={open}
      handleClose={handleClose}
      handleProceed={handleProceed}
      proceedButtonDisabled={!selectedFolder || !selectedSurveyType || !state.isValid || state.isEmpty || csvLoading}
      {...remProps}
    >
      <Grid container={true} spacing={2} justifyContent={'space-between'}>
        <Grid size={12}>
          <CreateSurveyForm
            csvLoading={csvLoading}
            csvImportSummary={csvImportSummary}
            csvPreviewRows={csvPreviewRows}
            sendInvitationsFormState={sendInvitationsFormState}
            formChangeHandlerGenerator={formChangeHandlerGenerator}
            formChangeHandleDeleteGenerator={formChangeHandleDeleteGenerator}
            addInvitations={addInvitations}
            clearInvitations={clearInvitations}
            addInvitationsCSV={addInvitationsCSV}
            addInvitationsCSVFromDrop={addInvitationsCSVFromDrop}
          />
        </Grid>
        <Grid size={8}>
          <PaiSelect value={selectedFolder} label={t('fileUploadFolder')} onChange={handleSelectedFolderChange}>
            {folders.map((option) => (
              <PaiMenuItem key={option.value} value={option.value}>
                {option.label}
              </PaiMenuItem>
            ))}
          </PaiSelect>
        </Grid>
        <Grid size={4}>
          <PaiSelect value={selectedSurveyType} label={t('surveyType')} onChange={handleSelectedSurveyTypeChange}>
            {surveyTypes.map((option) => (
              <PaiMenuItem key={option.value} value={option.value}>
                {option.label}
              </PaiMenuItem>
            ))}
          </PaiSelect>
        </Grid>
        <Grid>
          <PaiCheckbox label={t('notSendEmail')} checked={notSendEmail} onChange={handleNotSendEmailChange} />
        </Grid>
      </Grid>
    </BasicActionDialog>
  );
};

export default CreateSurveyDialog;
